import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Security,
  Delete,
  Visibility,
  VisibilityOff,
  ArrowBack
} from '@mui/icons-material';
import merchantAPI from '../../../services/merchantAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { useNavigate } from 'react-router-dom';
import FlutterServicesAPI from '../../../services/flutterServices';
import { AutoCloseAlert } from '../../Common';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import useRouteMeta from '../../../hooks/useRouteMeta';

const SecurityPage = () => {
  const navigate = useNavigate();
  useRouteMeta();
  const { logout } = useMerchantAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState({});

  const [passwordError, setPasswordError] = useState('');
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  const { hasPermission } = useMerchantPermissions();
  const canSecurity = hasPermission('Merchant Profile', 'change_password');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await merchantAPI.getProfile();
      setProfile(response.data.merchant);
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
    if (passwordError) setPasswordError('');
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    if (!passwordData.current_password.trim()) { setPasswordError('Current password is required'); return; }
    if (!passwordData.new_password.trim()) { setPasswordError('New password is required'); return; }
    if (passwordData.current_password === passwordData.new_password) { setPasswordError('New password must be different from current password'); return; }
    if (passwordData.new_password !== passwordData.confirm_password) { setPasswordError('New passwords do not match'); return; }
    if (passwordData.new_password.length < 8) { setPasswordError('New password must be at least 8 characters long'); return; }

    setLoading(true);
    try {
      await merchantAPI.changePassword({ current_password: passwordData.current_password, new_password: passwordData.new_password });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setPasswordError('');
      setShowPasswordForm(false);
      setSuccess('Password changed successfully!');
      await loadProfile();
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteAccountLoading(true);
      await merchantAPI.deleteAccount();
      setSuccess('Account deleted successfully');
      setDeleteAccountDialog(false);
      setTimeout(() => { logout(); navigate('/login'); }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  return (
    <Container className="mt-mb-0" maxWidth="lg" sx={{ mt: { xs: 0, sm: 0, md: 4 }, mb: { xs: 0, sm: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 0.5, sm: 2, md: 4 }, pt: { xs: 0, sm: 3, md: 3 }, boxShadow: 'none', border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' }, bgcolor: { xs: 'transparent', sm: 'white', md: 'white' }, borderRadius: '8px' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={{ xs: 0.5, sm: 0 }}>
          <Button onClick={() => navigate('/dashboard')}
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
            Back
          </Button>
          <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>Security</Typography>
        </Box>

        <Box display="flex" justifyContent="flex-start" alignItems="center" mb={1} mt={0.1} px={{ xs: 1, sm: 0 }}>
          <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', color: '#101828' }}>
            Security Settings
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <AutoCloseAlert severity="success" message={success} show={!!success} onClose={() => setSuccess('')} autoCloseDelay={4000} />

        <Box mt={2} px={{ xs: 0, sm: 0 }}>
          <Grid container spacing={3}>
            {/* Change Password section hidden as requested
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{
                borderRadius: '16px',
                boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.08)',
                border: 'none',
                bgcolor: 'white',
                height: '100%'
              }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Box sx={{ width: 48, height: 48, backgroundColor: 'rgba(101, 27, 198, 0.1)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src="/assets/img/website/icons/security-icon.svg" alt="Security" style={{ width: '24px', height: '24px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#101828', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', mb: 0.5 }}>Change Password</Typography>
                      <Typography sx={{ color: '#6A7282', fontFamily: 'var(--font-montserrat)', fontWeight: 400, fontSize: '13px' }}>
                        Last changed: {profile.password_updated_at ? new Date(profile.password_updated_at).toLocaleDateString('en-GB') : 'Never'}
                      </Typography>
                    </Box>
                  </Box>

                  {canSecurity && !showPasswordForm && (
                    <Button variant="contained" fullWidth onClick={() => setShowPasswordForm(true)}
                      sx={{ backgroundColor: '#651BC6', color: '#ffffff', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', textTransform: 'uppercase', borderRadius: '14px', boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.3)', height: '42px', '&:hover': { backgroundColor: '#5616a8', boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.3)' } }}>
                      CHANGE PASSWORD
                    </Button>
                  )}

                  {canSecurity && showPasswordForm && (
                    <Box mt={2}>
                      <form autoComplete="off" style={{ display: 'none' }} aria-hidden="true">
                        <input type="text" name="username" />
                        <input type="password" name="password" />
                      </form>

                      {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}

                      <Box component="div" sx={{ maxWidth: '100%' }}>
                        <Typography className="profile-input-label">Current Password</Typography>
                        <TextField fullWidth className="profile-form-control" type={showCurrentPassword ? 'text' : 'password'} placeholder="Enter current password"
                          value={passwordData.current_password} onChange={(e) => handlePasswordInputChange('current_password', e.target.value)}
                          autoComplete="off"
                          InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" sx={{ color: '#6b7280' }}>{showCurrentPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }}
                          sx={{ mb: 2 }}
                        />
                        <Typography className="profile-input-label">New Password</Typography>
                        <TextField fullWidth className="profile-form-control" type={showNewPassword ? 'text' : 'password'} placeholder="Enter new password"
                          value={passwordData.new_password} onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                          autoComplete="off" name="new-pw-2"
                          InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: '#6b7280' }}>{showNewPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }}
                          sx={{ mb: 2 }}
                        />
                        <Typography className="profile-input-label">Confirm New Password</Typography>
                        <TextField fullWidth className="profile-form-control" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm new password"
                          value={passwordData.confirm_password} onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                          autoComplete="off" name="new-pw-3"
                          InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: '#6b7280' }}>{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }}
                          sx={{ mb: 2 }}
                        />
                      </Box>

                      <Box sx={{ mt: 1, p: 2, bgcolor: '#f9fafb', borderRadius: 1.5, border: '1px solid #e5e7eb', mb: 2, maxWidth: '100%' }}>
                        <Typography sx={{ mb: 1.5, fontWeight: 500, fontSize: '14px', fontFamily: 'var(--font-montserrat-semibold)', color: '#1E2939' }}>Password Requirements:</Typography>
                        <Typography sx={{ fontSize: '14px', mb: 0.8, fontFamily: 'var(--font-montserrat-medium)', color: '#6A7282' }}>• At least 8 characters long</Typography>
                        <Typography sx={{ fontSize: '14px', mb: 0.8, fontFamily: 'var(--font-montserrat-medium)', color: '#6A7282' }}>• Must be different from current password</Typography>
                        <Typography sx={{ fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)', color: '#6A7282' }}>• New password and confirm password must match</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexDirection: 'column' }}>
                        <Button variant="contained" onClick={handlePasswordChange}
                          disabled={loading || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
                          sx={{ background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', color: 'white', textTransform: 'none', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', boxShadow: 'none', width: '100%', height: '48px', borderRadius: '15.15px', '&:hover': { background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', opacity: 0.9, boxShadow: 'none' }, '&:disabled': { backgroundColor: '#d1d5db', color: '#ffffff' } }}>
                          {loading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                        </Button>
                        <Button variant="outlined" onClick={() => { setPasswordData({ current_password: '', new_password: '', confirm_password: '' }); setShowCurrentPassword(false); setShowNewPassword(false); setShowConfirmPassword(false); setPasswordError(''); setShowPasswordForm(false); }}
                          sx={{ backgroundColor: '#f3f4f6', color: 'black', borderColor: 'black', textTransform: 'none', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', boxShadow: 'none', width: '100%', height: '48px', borderRadius: '15.15px', '&:hover': { borderColor: 'black', backgroundColor: '#f3f4f6' } }}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            */}

            {/* Danger Zone Card */}
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{
                borderRadius: '16px',
                boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.08)',
                border: '1px solid #FEE2E2',
                backgroundColor: '#FEF2F2',
                height: '100%'
              }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ width: 48, height: 48, backgroundColor: '#FEE2E2', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src="/assets/img/website/icons/danger-icon.svg" alt="Danger" style={{ width: '24px', height: '24px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#DC2626', fontFamily: 'var(--font-montserrat-bold)', fontWeight: 700, fontSize: '16px' }}>Danger Zone</Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ color: '#4A5565', fontFamily: 'var(--font-montserrat)', fontWeight: 400, fontSize: '13px', mb: 3 }}>
                    This action cannot be undone. Once you delete your account, all your data will be permanently removed from our servers.
                  </Typography>
                  <Button variant="outlined" fullWidth onClick={() => setDeleteAccountDialog(true)}
                    sx={{ borderColor: '#DC2626', color: '#DC2626', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', textTransform: 'uppercase', borderRadius: '14px', height: '42px', '&:hover': { borderColor: '#b91c1c', backgroundColor: 'transparent', color: '#b91c1c' } }}>
                    DELETE ACCOUNT
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>


        </Box>
      </Paper>

      {/* Delete Account Dialog */}
      <Dialog open={deleteAccountDialog} onClose={() => setDeleteAccountDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            borderRadius: '22px', m: 2,
            maxWidth: {
              xs: 'calc(100% - 30px)', // 📱 mobile
              sm: 'calc(100% - 60px)',
              md: '500px',             // 💻 desktop
              lg: '500px'              // making it slightly smaller because it's just a confirmation
            },
            width: '100%', // important so maxWidth applies properly
            mx: 'auto'
          }
        }} >
        <DialogContent sx={{ px: { xs: 2, sm: 1 }, py: { xs: 4, sm: 3 }, overflow: 'auto', textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: { xs: '17px', sm: '18px' }, color: '#101828', mb: 1.5 }}>
            Are you sure you want to delete your account?
          </Typography>
          <Typography sx={{ fontFamily: 'var(--font-montserrat-regular)', fontSize: '14px', color: '#6A7282', mb: 2.5 }}>
            This action will also log you out immediately.
          </Typography>
          <Box sx={{ backgroundColor: '#fff4e5', py: 1.5, px: 2.5, borderRadius: '10px', display: 'inline-block', textAlign: 'left' }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontSize: '13px', color: '#663c00', mb: 0.5 }}>
              This action cannot be undone!
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-regular)', fontSize: '13px', color: '#663c00' }}>
              Once you delete your account, all your data including:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px', fontFamily: 'var(--font-montserrat-regular)', fontSize: '13px', color: '#663c00' }}>
              <li>Profile information</li>
              <li>AI generations</li>
              <li>{FlutterServicesAPI.getCreditLabel()} balance</li>
              <li>Brand logos</li>
              <li>All other account data</li>
            </ul>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontSize: '13px', color: '#663c00' }}>
              Will be permanently removed and cannot be recovered.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, justifyContent: 'center', gap: 1 }}>
          <Button variant="outlined" onClick={() => setDeleteAccountDialog(false)}
            sx={{ borderColor: '#E5E7EB', color: '#364153', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', px: 2, py: 1, borderRadius: '13.15px', textTransform: 'none', minHeight: '42px', minWidth: '120px' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteAccount} disabled={deleteAccountLoading}
            sx={{ backgroundColor: '#ef4444', color: 'white', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', px: 2, py: 1, borderRadius: '13.15px', textTransform: 'none', minHeight: '42px', minWidth: '120px', boxShadow: 'none', '&:hover': { backgroundColor: '#dc2626', boxShadow: 'none' }, '&:disabled': { backgroundColor: '#fca5a5', color: '#fef2f2' } }}>
            {deleteAccountLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SecurityPage;
